import { useCallback, useState } from "react";
import type { GridController } from "../focus/gridController";

/**
 * Marks which edges have content scrolled under them, so pinned columns draw
 * their edge border only then. Writes data attributes; never renders.
 */
export const updateOverflowEdges = (viewport: HTMLElement) => {
    const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth;
    viewport.toggleAttribute("data-overflow-start", viewport.scrollLeft > 0);
    viewport.toggleAttribute(
        "data-overflow-end",
        viewport.scrollLeft < maxScrollLeft - 1,
    );
};

const scheduledViewports = new WeakSet<HTMLElement>();

const isInPinnedCell = (target: EventTarget | null, viewport: HTMLElement) => {
    const pinnedCell =
        target instanceof Element ? target.closest("[data-pin]") : null;
    return pinnedCell !== null && viewport.contains(pinnedCell);
};

/**
 * `updateOverflowEdges` in the next frame, once however often it's asked.
 * Its reads force a layout; called mid-commit, that layout is thrown away by
 * the rest of the commit's DOM writes. In a frame callback it is the layout
 * the frame needs anyway, still before paint.
 */
export const scheduleOverflowEdges = (viewport: HTMLElement) => {
    if (scheduledViewports.has(viewport)) {
        return;
    }
    scheduledViewports.add(viewport);
    requestAnimationFrame(() => {
        scheduledViewports.delete(viewport);
        if (viewport.isConnected) {
            updateOverflowEdges(viewport);
        }
    });
};

/**
 * Ref callback for the scroll viewport: registers it with the controller,
 * publishes its content width for fill columns, and keeps the overflow edge
 * attributes current through one passive scroll listener that skips vertical
 * scrolls (`ColumnSizeVars` updates them when widths change), and undoes the
 * horizontal jump native focus gives a pinned cell. The element is also
 * returned as state: the body's virtualizer mounts before the viewport ref
 * attaches and needs a render that sees it.
 */
export const useViewportObservers = (controller: GridController) => {
    const [viewportElement, setViewportElement] =
        useState<HTMLDivElement | null>(null);
    const viewportRef = useCallback(
        (viewport: HTMLDivElement | null) => {
            controller.setViewport(viewport);
            setViewportElement(viewport);
            if (!viewport) {
                return undefined;
            }
            // Not read here: a scroll position read forces a layout mid-commit.
            let lastScrollLeft = 0;
            const handleScroll = () => {
                if (viewport.scrollLeft === lastScrollLeft) {
                    return;
                }
                lastScrollLeft = viewport.scrollLeft;
                updateOverflowEdges(viewport);
            };
            // Native focus (Tab, `focus()` without `preventScroll`) scrolls a
            // sticky pinned cell to its unpinned spot, at the grid's far end,
            // after focusout and before focusin. Focus arriving from outside
            // the grid falls back to the last scroll event's position.
            let scrollLeftBeforeFocus: number | null = null;
            const handleFocusOut = (event: FocusEvent) => {
                scrollLeftBeforeFocus = isInPinnedCell(
                    event.relatedTarget,
                    viewport,
                )
                    ? viewport.scrollLeft
                    : null;
            };
            const handleFocusIn = (event: FocusEvent) => {
                const scrollLeft = scrollLeftBeforeFocus ?? lastScrollLeft;
                scrollLeftBeforeFocus = null;
                if (
                    isInPinnedCell(event.target, viewport) &&
                    viewport.scrollLeft !== scrollLeft
                ) {
                    viewport.scrollLeft = scrollLeft;
                }
            };
            viewport.addEventListener("scroll", handleScroll, {
                passive: true,
            });
            viewport.addEventListener("focusout", handleFocusOut);
            viewport.addEventListener("focusin", handleFocusIn);
            const resizeObserver = new ResizeObserver((entries) => {
                const entry = entries[entries.length - 1];
                if (entry) {
                    controller.containerWidth.set(
                        Math.floor(entry.contentRect.width),
                    );
                }
                updateOverflowEdges(viewport);
            });
            resizeObserver.observe(viewport);
            scheduleOverflowEdges(viewport);
            return () => {
                viewport.removeEventListener("scroll", handleScroll);
                viewport.removeEventListener("focusout", handleFocusOut);
                viewport.removeEventListener("focusin", handleFocusIn);
                resizeObserver.disconnect();
                controller.setViewport(null);
            };
        },
        [controller],
    );
    return { viewportRef, viewportElement };
};
